/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, {
    useState, useReducer, useEffect, useCallback, useRef, useContext,
} from 'react';
import {
    object, string, shape, arrayOf, number, func, bool, oneOfType, node,
} from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import DesktopMobile from '../../../../../../../helpers/DesktopMobile/DesktopMobile';
import MobileCYOProductItemsVTwo from './MobileCYOProductItemsVTwo';
import DesktopCYOProductItemsVTwo from './DesktopCYOProductItemsVTwo';
import { getSelectedInlineAddons } from '../../../../../../../../state/ducks/App/App-Selectors';
import { CrossMerch } from '../../../../../CrossMerchContainer/CrossMerchContainer';
import { getBaseCode } from '../../../../../../../helpers/tracking/product/dataLayerProductHelpers';
import YotpoClickEvent from '../../../StandardProductsPage/YotpoClickEvent';

const useStyles = makeStyles((theme) => ({
    prReviewDisplay: {
        '& .p-w-r .pr-review-snapshot .pr-review-snapshot-simple .pr-review-snapshot-snippets-headline': {
            marginRight: '10px',
        },
        '& .pr-snippet': {
            display: 'flex',
            minWidth: 'fit-content',
        },
        '& .p-w-r .modal__body .pr-read-review-flagging-in .pr-rd-helpful-action-btn-group': {
            display: 'flex',
        },
        '& .pr-rd-helpful-action-group': {
            display: 'flex',
        },
        [theme?.breakpoints?.down('980')]: {
            '& .p-w-r .pr-review-snapshot .pr-snippet .pr-snippet-stars-png .pr-star-v4': {
                height: '15px',
                width: '15px',
            },
            '& .p-w-r .pr-review-snapshot.pr-snapshot-mobile .pr-review-snapshot-simple .pr-snippet-rating-decimal': {
                display: 'none',
            },
            '& .pr-review-snapshot-snippets': {
                justifyContent: 'left',
            },
            '& .p-w-r .pr-review-display .pr-rd-helpful-action .pr-rd-helpful-text': {
                display: 'block',
            },
            '& .pr-rd-flag-review-container': {
                display: 'block',
            },
        },
        [theme?.breakpoints?.down('sm')]: {
            '& .pr-snippet': {
                flexDirection: 'column',
            },
        },
        [theme?.breakpoints?.down('xs')]: {
            '& .p-w-r .pr-review-display .pr-rd-helpful-action .pr-rd-helpful-text': {
                display: 'none',
            },
            '& .pr-rd-flag-review-container': {
                display: 'none',
            },
            '& .pr-snippet': {
                flexDirection: 'column',
            },
        },
    },
    prQuestionDisplay: {
        '& .p-w-r .pr-waqwaa': {
            maxWidth: '100%',
        },
        '& .p-w-r .pr-qa-display .pr-btn-ask-question .pr-cross-icon': {
            display: 'none',
        },
    },
    hidePRStarDistribution: {
        '& .p-w-r .pr-review-snapshot.pr-review-enhanced .pr-review-snapshot-block-container .pr-review-snapshot-block:last-child': {
            display: 'none',
        },
    },
}));

const CYOProductItemsVTwo = ({
    cyoData,
    handleMiniCartModalClick,
    nonCountableKey,
    selectedItemId,
    brandId,
    product,
    cartError,
    hasWine,
    handleMobileAgeVerification,
    track,
    categoryData,
    deliverySLA,
    selectedAddons,
    showErrorMessage,
    hideMenu,
    useGuided,
    forcedIndex,
    url,
    addonContainer,
    hasAddons,
    setSelectedComponents,
    presentationFamily,
}) => {
    const classes = useStyles();
    const [totalProductsSelected, setTotalProductsSelected] = useState(0);
    const [maxSelectableForSku, setMaxSelectableForSku] = useState(0);
    const { featureFlags = {} } = useContext(CrossMerch);
    const dispatch = useDispatch();
    const { clickableDivRef } = YotpoClickEvent(dispatch);
    // Completed will be used for guided process
    // Will be in format: {groupName: {completed: bool, itemID: {productCode: string, quantity: number}}}
    const [selectedItems, setSelectedItems] = useState({});
    // Will have format: {groupName: name, itemID: id, productCode: code}
    const [selectedBand, setSelectedBand] = useState({ groupName: undefined, itemID: undefined, productCode: undefined });
    const [activeIndex, setActiveIndex] = useState(false);
    const [nonCountableItems, setNonCountableItems] = useState([]);
    const errorTimeout = useRef();
    const selectAddonsV3 = useSelector(getSelectedInlineAddons);
    const pdpInlineAddon = featureFlags['is-inline-addons-pdp-enabled'] &&  selectAddonsV3?.length;
    const cyoPdpYotpoEnabled = Boolean(featureFlags?.['is-pdp-yotpo-enabled']);
    const isPdpPowerReviewsEnabled = Boolean(featureFlags?.['is-pdp-reviews-enabled']);
    const isPowerReviewEnabled = Boolean(featureFlags?.['is-power-review-enabled']);
    /**
    *@description Compiles the selected items into desired format and returns the max
        number selected
    *@returns {[number, Array]} Number is the number of items selected, not including bands or nonCountables.
        Array is an array of objects with the format:
        {
            brandCode: brandId,
            productCode: id,
            productId: legacyId,
            quantity: number,
            componentType: 'CYO'
        }
    */
    const compileSelectedItems = () => {
        const productArray = [...nonCountableItems];

        // Include the selected band
        if (selectedBand.groupName) {
            productArray.push({
                brandCode: brandId,
                productCode: selectedBand.productCode,
                productId: selectedBand.itemID,
                quantity: 1,
                componentType: 'CYO',
            });
        }

        Object.values(selectedItems).forEach((groupSelection) => {
            Object.entries(groupSelection).forEach(([itemID, item]) => {
                if (item.quantity) {
                    productArray.push({
                        brandCode: brandId,
                        productCode: item.productCode,
                        productId: itemID,
                        quantity: item.quantity,
                        componentType: 'CYO',
                    });
                }
            });
        });
        return productArray;
    };

    useEffect(() => {
        setSelectedComponents(compileSelectedItems());
    }, [selectedItems]);

    // Will have format: {itemID: errorMessage}
    // Reducer is needed as clearing of error takes place within a timeout and cannot reference most recent state
    const [error, setError] = useReducer((state, action) => {
        const { [action.itemID]: item, ...rest } = state;
        switch (action.type) {
            case 'addDisabledError':
                return { ...state, [action.itemID]: action.message, bannerError: action.message };
            case 'removeDisabledError':
                return { ...state, bannerError: null };
            case 'add':
                return { ...state, [action.itemID]: action.message };
            case 'remove':
                return rest;
            default:
                return state;
        }
    }, {});

    // Don't allow add to cart if the product is CYO/PYO and doesnt have data in it.
    if (
        product?.productType === 'CUSTOM_ASSORTMENT'
        && (!cyoData?.cyoGroup || cyoData?.cyoGroup.length === 0)
    ) {
        return showErrorMessage('center');
    }

    const handleSelectBand = (groupName, bandID, productCode) => {
        if (groupName && bandID) {
            setSelectedBand({ groupName, itemID: bandID, productCode });
        }
    };

    const handleRemoveBand = () => {
        setSelectedBand({ groupName: undefined, itemID: undefined, productCode: undefined });
    };

    const getMaxSelectable = () => {
        let num = 0;
        // Because the maxQuantity does not take into account the
        // non-countable items or bands, we cannot here either for consistency
        if (!cyoData.maxQuantity) {
            cyoData.cyoGroup.forEach((group) => {
                if (group.type !== nonCountableKey && group.type !== 'cyoBands') {
                    num += group.numberofUnitsRequiredForComponent;
                }
            });
        } else {
            num = cyoData.maxQuantity;
        }
        return num;
    };

    /**
     *
     * @param {string} groupName the name of the group that the selection is made in
     * @returns {object} an object that describes selections in format:
     *  {
     *      totalNumberSelected: number of total selections,
     *      groupNumberSelected: number of selections within group,
     *      groupFlavorsSelected: number of flavors selected within group
     *  }
     */
    const getGroupSelectionData = (groupName) => {
        let totalNumberSelected = 0;
        let groupNumberSelected = 0;
        let groupFlavorsSelected = 0;
        Object.entries(selectedItems).forEach(([name, items]) => {
            const addToGroupTotal = name === groupName;
            Object.values(items).forEach((item) => {
                if (item.quantity) {
                    totalNumberSelected += item.quantity;
                    if (addToGroupTotal) {
                        groupFlavorsSelected += 1;
                        groupNumberSelected += item.quantity;
                    }
                }
            });
        });
        return { totalNumberSelected, groupNumberSelected, groupFlavorsSelected };
    };

    /*
        The below '-' character is a special '-' character. The '-' coming from the CMS, was not matching
        my native '-' character, so I pulled it from the console, and it turns out the CMS is delivering a
        different Unicode version of '-'. I imagine it is an OS thing. I got that character from a unicode
        site, so be weary if you change this code. If the slicing stops working, it may be because your machine
        replaced this special '-' with a non-special '-'.
        Special: '–'
        Not:     '-'
        TODO: Figure out how to make more robust
    */
    const getPrettyGroupName = (name) => (name.indexOf('–') > 0 ? name.slice(0, name.indexOf('–') - 1) : name);

    const handleClearError = (itemID) => {
        setError({ type: 'remove', itemID });
    };

    const handleItemRemove = (cyoGroup, itemID) => {
        const groupName = cyoGroup?.name || '';
        const group = selectedItems[groupName] || {};
        const item = group[itemID] || {};
        const numberItems = item.quantity || 0;
        setTotalProductsSelected(totalProductsSelected - numberItems);
        setSelectedItems({
            ...selectedItems,
            [groupName]: {
                ...group,
                [itemID]: {
                    ...item,
                    quantity: 0,
                },
            },
        });
    };

    const handleDisabledInteraction = (itemID) => {
        setError(
            { type: 'addDisabledError', itemID, message: 'PLEASE CHOOSE A DECORATIVE BAND' },
        );
        if (typeof window !== 'undefined') {
            errorTimeout.current = setTimeout(() => {
                setActiveIndex(0);
                errorTimeout.current = setTimeout(() => {
                    setError({
                        type: 'removeDisabledError',
                    });
                    // Reset active index so it can be switched back to 0, otherwise it will not re-render
                    setActiveIndex(null);
                }, 2000);
            }, 1500);
        }
    };
    const checkItems = (items, data) => {
        const matchedDetails = [];
        const allMatch = items.every((item) => {
            const { name = '', minimumUnitsRequiredForComponent = '' } = item || {};
            const dataEntry = data?.[name];
            if (!dataEntry && name) {
                matchedDetails.push({ name, unitRequired: minimumUnitsRequiredForComponent });
                return false;
            }
            const totalQuantity = Object.values(dataEntry)
                .filter((prop) => typeof prop === 'object' && 'quantity' in prop)
                .reduce((sum, prop) => sum + prop.quantity, 0);

            if (totalQuantity >= minimumUnitsRequiredForComponent) {
                return true;
            }
            matchedDetails.push({ name, unitRequired: minimumUnitsRequiredForComponent });
            return false;
        });
        return {
            allMatch,
            matchedDetails,
        };
    };
    // productCode is item.id, while itemID is item.legacyId
    const handleItemSelection = (cyoGroup, itemID, productCode, incrementNumber) => {
        const groupName = cyoGroup?.name || '';
        let shouldAllowModification = true;
        let groupCompleted = false;
        const filteredItems = cyoData?.cyoGroup?.filter((item) => item?.minimumUnitsRequiredForComponent > 0 && item?.type !== 'cyoBands');
        const selectedItem = filteredItems.find((item) => item.name === cyoGroup.name);
        const totalUnitRequired = filteredItems.reduce((total, item) => total + item.minimumUnitsRequiredForComponent, 0);

        // Only allow modification if not exceeding maximum limitations and not going negative
        if (incrementNumber > 0) {
            const { totalNumberSelected, groupNumberSelected, groupFlavorsSelected } = getGroupSelectionData(groupName);
            // The total number of allowable items regardless of group or flavor
            const totalAllowableItems = cyoData.maxQuantity || 999;
            // The number of allowable items per group
            const groupAllowableItems = cyoGroup?.numberofUnitsRequiredForComponent || 999;
            const allowAddedItem = totalAllowableItems - totalUnitRequired;
            // The number of allowable items per item
            const numFlavorsAllowed = cyoGroup?.maxFlavorsForComponent || 999;

            const unitRequiredData = filteredItems.length > 0 ? checkItems(filteredItems, selectedItems) : {};

            if (totalUnitRequired > 0 && !selectedItem && !unitRequiredData?.allMatch && allowAddedItem === totalNumberSelected) {
                shouldAllowModification = false;
                return setError(
                    { type: 'add', itemID, message: `Please choose ${unitRequiredData.matchedDetails[0]?.name}` },
                );
            }
            if (totalNumberSelected + incrementNumber > totalAllowableItems) {
                shouldAllowModification = false;
                return setError({ type: 'add', itemID, message: `You can select up to ${totalAllowableItems} items` });
            }
            if (groupNumberSelected + incrementNumber > groupAllowableItems) {
                shouldAllowModification = false;
                return setError({ type: 'add', itemID, message: `You can select up to ${groupAllowableItems}` });
            }
            if (groupNumberSelected + incrementNumber === groupAllowableItems) {
                groupCompleted = true;
            }

            if (
                groupFlavorsSelected >= numFlavorsAllowed
                && (
                    !selectedItems[groupName]
                    || !selectedItems[groupName][itemID]
                    || selectedItems[groupName][itemID].quantity === 0
                )
            ) {
                shouldAllowModification = false;
                return setError({ type: 'add', itemID, message: `You can select up to ${numFlavorsAllowed} different flavors` });
            }
        } else if (
            !selectedItems[groupName]
            || !selectedItems[groupName][itemID]
            || selectedItems[groupName][itemID].quantity === 0
            || selectedItems[groupName][itemID].quantity - incrementNumber < 0
        ) {
            shouldAllowModification = false;
            return null;
        }

        if (shouldAllowModification) {
            const groupSelection = selectedItems[groupName] || {};
            const itemNumber = groupSelection[itemID]?.quantity || 0;
            const newTotal = itemNumber + incrementNumber;
            if (Number.isInteger(newTotal)) {
                setTotalProductsSelected(totalProductsSelected + incrementNumber);
                setSelectedItems({
                    ...selectedItems,
                    [groupName]: {
                        ...groupSelection,
                        // If previously complete, maintain that state.
                        completed: groupSelection.completed ? true : groupCompleted,
                        [itemID]: {
                            quantity: itemNumber + incrementNumber,
                            productCode,
                        },
                    },
                });
            }
        }
        return null;
    };

    const resetState = useCallback(() => {
        setSelectedItems({});
        setSelectedBand({ groupName: null, itemID: null, productCode: null });
        setMaxSelectableForSku(getMaxSelectable());
        setTotalProductsSelected(0);
    }, [cyoData]);

    // Reset state upon data change
    useEffect(() => {
        resetState();
    }, [selectedItemId]);

    // On unmount, clear timeouts to prevent memory leak
    useEffect(() => () => {
        if (typeof window !== 'undefined') {
            clearTimeout(errorTimeout);
        }
    });

    // Handles changed activeIndex if guided
    useEffect(() => {
        if (useGuided && cyoData?.cyoGroup?.length) {
            let index = 0;
            for (let i = 0; i < cyoData.cyoGroup.length; i += 1) {
                const name = cyoData.cyoGroup[i].name;
                if (selectedBand.groupName === name || selectedItems[name]?.completed) {
                    index += 1;
                }
            }
            // We want to explicity set it for the first one
            if (index !== 0 && index < cyoData?.cyoGroup?.length) {
                setActiveIndex(index);
            }
        }
    }, [selectedItems, selectedBand]);

    // Handles forced index from parent
    useEffect(() => {
        if (typeof forcedIndex === 'number') {
            setActiveIndex(forcedIndex);
        } else {
            setActiveIndex(false);
        }
    }, [forcedIndex]);

    // Handles maintaining the nonCountable Items such as tongs and turkey
    useEffect(() => {
        const productArray = [];
        // Include any non-countable items such as turkey/tongs
        cyoData.cyoGroup.forEach((group) => {
            const item = group.components[0] || {};
            if (group.type === nonCountableKey) {
                productArray.push({
                    brandCode: brandId,
                    productCode: item.id,
                    productId: item.legacyId,
                    quantity: 1,
                    componentType: 'CYO',
                });
            }
        });
        setNonCountableItems(productArray);
    }, [cyoData]);

    // handle V3 inline pdp addons values
    const getAddons = () => {
        const addonsComponents = [];
        selectAddonsV3.forEach((addon) => {
            const addonItem = {
                brandCode: brandId,
                productCode: addon.partNumber,
                componentType: 'ADDON',
                productId: addon.legacyId,
                quantity: addon.qty,
            };
            addonsComponents.push(addonItem);
        });
        return addonsComponents;
    };

    const showAggregatedRating = () => (featureFlags['is-show-product-reviews-aggregated-star-rating'] ? 'gold-stars' : 'pr-Review-Image-Display-Disclaimer pr-reviewdisplay-no-aggregated-star-rating black-stars');
    const showRatingDistribution = () => (featureFlags['is-show-product-reviews-star-distribution'] ? '' : classes.hidePRStarDistribution);

    return (
        <>
            <DesktopMobile
                desktop={() => (
                    <DesktopCYOProductItemsVTwo
                        cyoData={cyoData}
                        handleMiniCartModalClick={handleMiniCartModalClick}
                        nonCountableKey={nonCountableKey}
                        selectedItemId={selectedItemId}
                        brandId={brandId}
                        product={product}
                        cartError={cartError}
                        hasWine={hasWine}
                        handleMobileAgeVerification={handleMobileAgeVerification}
                        track={track}
                        categoryData={categoryData}
                        deliverySLA={deliverySLA}
                        selectedAddons={pdpInlineAddon ? getAddons() : selectedAddons}
                        showErrorMessage={showErrorMessage}
                        totalProductSelected={totalProductsSelected}
                        maxSelectableForSku={maxSelectableForSku}
                        compileSelectedItems={compileSelectedItems}
                        addonContainer={addonContainer}
                        handleItemSelection={handleItemSelection}
                        handleSelectBand={handleSelectBand}
                        selectedBand={selectedBand}
                        handleRemoveBand={handleRemoveBand}
                        getPrettyGroupName={getPrettyGroupName}
                        selectedItems={selectedItems}
                        selectionError={error}
                        clearError={handleClearError}
                        handleDisabledInteraction={handleDisabledInteraction}
                        removeItem={handleItemRemove}
                        hideMenu={hideMenu}
                        useGuided={useGuided}
                        activeIndex={activeIndex}
                        hasAddons={hasAddons}
                    />
                )}
                mobile={() => (
                    <MobileCYOProductItemsVTwo
                        cyoData={cyoData}
                        handleMiniCartModalClick={handleMiniCartModalClick}
                        nonCountableKey={nonCountableKey}
                        selectedItemId={selectedItemId}
                        brandId={brandId}
                        product={product}
                        cartError={cartError}
                        hasWine={hasWine}
                        handleMobileAgeVerification={handleMobileAgeVerification}
                        track={track}
                        categoryData={categoryData}
                        deliverySLA={deliverySLA}
                        selectedAddons={pdpInlineAddon ? getAddons() : selectedAddons}
                        showErrorMessage={showErrorMessage}
                        totalProductSelected={totalProductsSelected}
                        maxSelectableForSku={maxSelectableForSku}
                        compileSelectedItems={compileSelectedItems}
                        addonContainer={addonContainer}
                        handleItemSelection={handleItemSelection}
                        handleSelectBand={handleSelectBand}
                        selectedBand={selectedBand}
                        handleRemoveBand={handleRemoveBand}
                        getPrettyGroupName={getPrettyGroupName}
                        selectedItems={selectedItems}
                        selectionError={error}
                        clearError={handleClearError}
                        handleDisabledInteraction={handleDisabledInteraction}
                        removeItem={handleItemRemove}
                        hideMenu={hideMenu}
                        useGuided={useGuided}
                        activeIndex={activeIndex}
                        handleAddedToCart={resetState}
                        hasAddons={hasAddons}
                    />
                )}
            />

            {isPdpPowerReviewsEnabled && isPowerReviewEnabled
                && (
                    <div style={{ margin: '0 10px' }}>
                        <div
                            id="pr-reviewdisplay"
                            className={`${classes.prReviewDisplay} pr-Review-Display-Snapshot-Mobile ${showAggregatedRating()} ${showRatingDistribution()}`}
                        />
                        <div
                            id="pr-questiondisplay"
                            className={classes.prQuestionDisplay}
                        />
                    </div>
                )}
            { isPdpPowerReviewsEnabled && cyoPdpYotpoEnabled && (
                <div
                    ref={clickableDivRef}
                    className="yotpo yotpo-main-widget"
                    data-product-id={getBaseCode(product.partNumber)}
                    // data-price={yotpoConfig?.price?.prices?.[1]?.value}
                    data-currency="USD"
                    data-name={product.name}
                    data-url={url}
                    data-image-url={`${product.image?.path}${product.image?.name}x.jpg`}
                />
            )}
        </>
    );
};

CYOProductItemsVTwo.propTypes = {
    selectedItemId: string,
    brandId: string.isRequired,
    handleMiniCartModalClick: func.isRequired,
    cyoData: shape({
        maxQuantity: number.isRequired,
        cyoGroup: arrayOf(
            shape({
                name: string.isRequired,
                components: arrayOf(
                    shape({
                        id: string.isRequired,
                        legacyId: string.isRequired,
                        name: string.isRequired,
                        image: shape({
                            name: string.isRequired,
                            path: string.isRequired,
                        }),
                    }),
                ).isRequired,
            }),
        ).isRequired,
    }).isRequired,
    product: object.isRequired,
    hasWine: bool,
    cartError: string.isRequired,
    handleMobileAgeVerification: func,
    nonCountableKey: string.isRequired,
    track: func,
    categoryData: shape({
        categoryId: string,
        categoryName: string,
        categoryPath: string,
    }),
    deliverySLA: string.isRequired,
    selectedAddons: arrayOf(shape({
        id: string,
        ageVerifyFlag: bool,
        partNumber: string,
        name: string,
    })),
    showErrorMessage: func.isRequired,
    url: string,
    hideMenu: bool,
    useGuided: bool,
    forcedIndex: oneOfType([number, bool]),
    addonContainer: node,
    hasAddons: bool,
    setSelectedComponents: func,
    presentationFamily: string,
};

CYOProductItemsVTwo.defaultProps = {
    addonContainer: () => null,
    selectedItemId: null,
    hasWine: false,
    handleMobileAgeVerification: () => {},
    track: () => {},
    categoryData: {},
    selectedAddons: [],
    hideMenu: false,
    useGuided: true, // Change if needed for testing
    forcedIndex: false,
    url: '',
    hasAddons: false,
    setSelectedComponents: () => {},
    presentationFamily: '',
};

export default CYOProductItemsVTwo;
